import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import ArticleCard from "@/components/ArticleCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Story {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  read_time: string;
  published_at: string;
  image_url: string | null;
  featured: boolean;
  slug: string;
}

const Index = () => {
  const [articles, setArticles] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  
  const category = searchParams.get('category');
  const isFiltered = !!category;
  
  useEffect(() => {
    fetchStories();
  }, [category]);

  useEffect(() => {
    // Scroll to articles section if there's a category filter
    if (category) {
      const articlesSection = document.getElementById('articles-section');
      if (articlesSection) {
        articlesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [category]);

  const fetchStories = async () => {
    try {
      let query = supabase
        .from('stories')
        .select('*')
        .order('published_at', { ascending: false });

      // Apply category filter if specified
      if (category && category !== 'latest') {
        query = query.eq('category', category);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching stories:', error);
        return;
      }

      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (!category) return 'Latest Articles';
    if (category === 'latest') return 'Latest Articles';
    return `${category} Articles`;
  };

  const getPageDescription = () => {
    if (!category) return 'Discover the latest music reviews, artist features, and industry insights';
    if (category === 'latest') return 'Discover the latest music reviews, artist features, and industry insights';
    return `Explore our ${category.toLowerCase()} content and stay updated with the music world`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <HeroSection onSubscribeClick={() => {}} />
      
      {/* Main Content */}
      <main className="container py-12" id="articles-section">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2">{getPageTitle()}</h2>
            <p className="text-muted-foreground">{getPageDescription()}</p>
          </div>
          <div className="hidden sm:flex items-center space-x-2">
            <Badge variant={!isFiltered ? "default" : "outline"}>
              <a href="/" className="text-current no-underline">All</a>
            </Badge>
            <Badge variant={category === 'Album Reviews' ? "default" : "outline"}>
              <a href="/?category=Album Reviews" className="text-current no-underline">Album Reviews</a>
            </Badge>
            <Badge variant={category === 'New Music' ? "default" : "outline"}>
              <a href="/?category=New Music" className="text-current no-underline">New Music</a>
            </Badge>
            <Badge variant={category === 'Industry News' ? "default" : "outline"}>
              <a href="/?category=Industry News" className="text-current no-underline">Industry News</a>
            </Badge>
            <Badge variant={category === 'Artist Features' ? "default" : "outline"}>
              <a href="/?category=Artist Features" className="text-current no-underline">Artist Features</a>
            </Badge>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">
                {isFiltered ? `No articles found in the "${category}" category.` : 'No articles found.'}
              </p>
              {isFiltered && (
                <a 
                  href="/" 
                  className="text-primary hover:underline inline-block mt-2"
                >
                  View all articles
                </a>
              )}
            </div>
          ) : (
            articles.map((article) => (
              <ArticleCard 
                key={article.id} 
                id={article.id}
                title={article.title}
                excerpt={article.excerpt}
                category={article.category}
                readTime={article.read_time}
                publishedAt={new Date(article.published_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                imageUrl={article.image_url}
                featured={article.featured}
                slug={article.slug}
              />
            ))
          )}
        </div>

        {/* Load More */}
        <div className="text-center">
          <Button variant="outline" size="lg">
            Load More Articles
          </Button>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;